import React, { useState } from 'react';
import { Button, SparkIcon, TooltipTrigger, Modal, SparkModalPortal, ProgressCircle, Spinner as SparkSpinner, BulkActionsBar as SparkBulkActionsBar } from '@spark';

export const DatasetsBulkActionsBar = ({
    selectedCount,
    totalCount,
    selectedDatasets = [],
    onSelectAll,
    onDeselectAll,
    onDownload,
    onDelete,
    disabled = false,
    isDownloading = false,
    downloadPercent = null,
}) => {
    const [showConfirm, setShowConfirm] = useState(false);

    const linkedDatasets = selectedDatasets.filter(
        dataset =>
            (dataset.group_and_usecase_names && Object.keys(dataset.group_and_usecase_names).length > 0) ||
            (dataset.linked_tasks && dataset.linked_tasks.length > 0)
    );
    const canDeleteAll = linkedDatasets.length === 0;
    const deletableCount = selectedCount - linkedDatasets.length;
    const isDeleteDisabled = disabled || deletableCount === 0;

    const handleDeleteClick = () => {
        setShowConfirm(true);
    };

    const handleConfirmDelete = () => {
        setShowConfirm(false);
        onDelete?.();
    };

    const deleteTooltip = !canDeleteAll && deletableCount === 0
        ? "All selected datasets are linked to tasks and cannot be deleted"
        : !canDeleteAll
            ? `${linkedDatasets.length} dataset${linkedDatasets.length > 1 ? 's are' : ' is'} linked to tasks`
            : "Delete selected datasets";

    const deleteLabel = !canDeleteAll && deletableCount > 0
        ? `Delete (${deletableCount})`
        : 'Delete';

    const confirmTitle = !canDeleteAll && deletableCount > 0
        ? `Delete ${deletableCount} dataset${deletableCount > 1 ? 's' : ''}?`
        : `Delete ${selectedCount} dataset${selectedCount > 1 ? 's' : ''}?`;

    return (
        <>
            <SparkBulkActionsBar
                selectedCount={selectedCount}
                totalCount={totalCount}
                onSelectAll={onSelectAll}
                onDeselectAll={onDeselectAll}
                actions={[]}
                disabled={disabled}
                endContent={
                    <>
                        <TooltipTrigger text="Download selected datasets">
                            <Button
                                variant="secondary"
                                size="small"
                                icon={isDownloading ? undefined : <SparkIcon slug="download" size="small" />}
                                onClick={onDownload}
                                disabled={disabled || isDownloading}
                            >
                                {isDownloading ? (
                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                                        {typeof downloadPercent === 'number'
                                            ? <ProgressCircle status="in-progress" value={Math.min(100, Math.round(downloadPercent))} />
                                            : <SparkSpinner size="small" />
                                        }
                                        Download
                                    </span>
                                ) : (
                                    'Download'
                                )}
                            </Button>
                        </TooltipTrigger>

                        <TooltipTrigger text={deleteTooltip}>
                            <Button
                                variant="secondary"
                                size="small"
                                icon={<SparkIcon slug="trash" size="small" />}
                                onClick={handleDeleteClick}
                                disabled={isDeleteDisabled}
                                disableWhenReadOnly
                            >
                                {deleteLabel}
                            </Button>
                        </TooltipTrigger>
                    </>
                }
            />

            <SparkModalPortal
                open={showConfirm}
                onClose={() => setShowConfirm(false)}
                size="text"
            >
                <Modal
                    className="datasets-delete-confirm-modal"
                    feedbackType="warning"
                    title={confirmTitle}
                    showSubtitle={false}
                    showScrollBar={false}
                    fixedHeight={false}
                    danger
                    primaryLabel="Delete"
                    primaryDisableWhenReadOnly
                    tertiaryLabel="Cancel"
                    onPrimaryClick={handleConfirmDelete}
                    onTertiaryClick={() => setShowConfirm(false)}
                    onCloseClick={() => setShowConfirm(false)}
                >
                    {!canDeleteAll && deletableCount > 0 ? (
                        <>
                            <p>{linkedDatasets.length} dataset{linkedDatasets.length > 1 ? 's are' : ' is'} linked to tasks and cannot be deleted.</p>
                            <p>Do you want to delete the remaining {deletableCount} dataset{deletableCount > 1 ? 's' : ''}?</p>
                        </>
                    ) : (
                        <p>This will permanently delete {selectedCount} dataset{selectedCount > 1 ? 's' : ''}.</p>
                    )}
                    <p style={{ color: 'var(--semantic-feedback-critical-on-surface)' }}>
                        This action cannot be undone.
                    </p>
                </Modal>
            </SparkModalPortal>
        </>
    );
};
